import { Box, Typography } from '@mui/material';
import { Check, X } from 'lucide-react';
import { useIntl, type IntlShape } from 'react-intl';
import { PatientIdText } from '~/containers/patient-list/shared';
import {
    formatImportMessage,
    importedSummaryText,
    skippedText,
    type QueueEntry,
} from '../model';
import { QUEUE_STATE_COLOR } from './importStatus';
import PatientLabel from './PatientLabel';

function skippedAfterLanding(entry: QueueEntry): boolean {
    return entry.status.kind === 'skipped' && entry.landed.length > 0;
}

/** Skipped after an attempt whose fate the wizard never learned: neither claim can be made. */
function skippedAfterLostAnswer(entry: QueueEntry): boolean {
    return (
        entry.status.kind === 'skipped' &&
        entry.landed.length === 0 &&
        entry.landingUnknown
    );
}

function verdictLine(
    intl: IntlShape,
    entry: QueueEntry
): { text: string; imported: boolean } {
    const { status } = entry;
    if (status.kind === 'imported') {
        return {
            text: formatImportMessage(
                intl,
                importedSummaryText(status.outcome)
            ),
            imported: true,
        };
    }
    return {
        text: formatImportMessage(
            intl,
            skippedText(entry.landed, 'summary', entry.landingUnknown)
        ),
        imported: false,
    };
}

type ImportAllDoneProps = {
    entries: readonly QueueEntry[];
};

/** Shown once every queue entry is imported or skipped: what happened to each. */
export default function ImportAllDone({ entries }: ImportAllDoneProps) {
    const intl = useIntl();
    const importedCount = entries.filter(
        (entry) => entry.status.kind === 'imported'
    ).length;
    const skipped = entries.filter((entry) => entry.status.kind === 'skipped');
    const skippedAfterLanded = skipped.filter(skippedAfterLanding);
    const skippedLostAnswer = skipped.filter(skippedAfterLostAnswer);
    // Only entries whose attempts were all answered may be reported as having sent nothing.
    const skippedSentNothing =
        skipped.length - skippedAfterLanded.length - skippedLostAnswer.length;

    return (
        <Box
            sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                py: 3.5,
                px: 5,
            }}
        >
            <Typography sx={{ fontSize: 20, fontWeight: 500 }}>
                {intl.formatMessage(
                    { id: 'patientList.import.done.headline' },
                    { imported: importedCount, total: entries.length }
                )}
            </Typography>
            <Typography
                sx={{ fontSize: 13.5, color: 'text.secondary', mt: 0.75 }}
            >
                {intl.formatMessage(
                    { id: 'patientList.import.done.subline' },
                    { imported: importedCount, skipped: skipped.length }
                )}
            </Typography>

            <Box
                sx={{
                    mt: 3,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                }}
            >
                {entries.map((entry) => {
                    const { drop } = entry;
                    const { text, imported } = verdictLine(intl, entry);
                    return (
                        <Box
                            key={drop.identity.dicomPatientId}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                py: 1.5,
                                px: 2,
                                borderBottom: 1,
                                borderColor: 'divider',
                                '&:last-of-type': { borderBottom: 0 },
                                fontSize: 13.5,
                            }}
                        >
                            {/* The name is the only cell allowed to shrink: the
                                id already middle-truncates and the verdict is
                                nowrap, so a long name ellipsises here. */}
                            <Typography
                                noWrap
                                sx={{ minWidth: 0, fontSize: 'inherit' }}
                            >
                                <PatientLabel identity={drop.identity} />
                            </Typography>
                            <PatientIdText id={drop.identity.dicomPatientId} />
                            <Box
                                sx={{
                                    ml: 'auto',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    fontSize: 12.5,
                                    color: QUEUE_STATE_COLOR[
                                        imported ? 'imported' : 'skipped'
                                    ],
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {imported ? (
                                    <Check size={13} />
                                ) : (
                                    <X size={13} />
                                )}
                                {text}
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            {skippedSentNothing > 0 && (
                <Typography
                    sx={{
                        mt: 2.25,
                        fontSize: 12.5,
                        color: 'text.disabled',
                        lineHeight: 1.5,
                    }}
                >
                    {skippedSentNothing === skipped.length
                        ? intl.formatMessage({
                              id: 'patientList.import.done.noneSentAll',
                          })
                        : intl.formatMessage(
                              { id: 'patientList.import.done.noneSentOther' },
                              { count: skippedSentNothing }
                          )}
                </Typography>
            )}

            {skippedLostAnswer.length > 0 && (
                <Typography
                    role="alert"
                    sx={{
                        mt: 2.25,
                        fontSize: 12.5,
                        color: 'warning.main',
                        lineHeight: 1.5,
                    }}
                >
                    {intl.formatMessage(
                        { id: 'patientList.import.done.landingUnknown' },
                        { count: skippedLostAnswer.length }
                    )}
                </Typography>
            )}

            {skippedAfterLanded.length > 0 && (
                <Typography
                    role="alert"
                    sx={{
                        mt: 2.25,
                        fontSize: 12.5,
                        color: 'warning.main',
                        lineHeight: 1.5,
                    }}
                >
                    {intl.formatMessage(
                        { id: 'patientList.import.done.someLanded' },
                        { count: skippedAfterLanded.length }
                    )}
                </Typography>
            )}
        </Box>
    );
}
